import { StudentLayout } from 'src/components/Layout/student/StudentLayout';
import { notifyAboutException, notifyAboutSuccess } from 'src/utils/notify';
import React, { FC, ReactElement, useRef, useState } from 'react';
import { DataTable } from 'src/components/DataTable';
import { Card } from 'src/components/Card/Card';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Helmet } from 'react-helmet-async';
import { Formik, FormikErrors, useFormik } from 'formik';
import { requireField } from 'src/constants/constants';
import { classNames } from 'primereact/utils';
import { SubjectCreate } from 'src/store/admin/subject/subject.types';
import { Toast } from 'primereact/toast';
import { LegacyModal } from 'src/components/Modal/LegacyModal';
import Loader from 'src/components/Loader';
import { InputText } from 'primereact/inputtext';
import { Toolbar } from 'src/components/Toolbar';
import { DeleteButton } from 'src/components/DeleteButton';
import {
    SubjectUpdateButton,
    SubjectUpdateForm,
} from 'src/pages/edu/Subjects/SubjectUpdateButton';
import {
    useCreateSubjectMutation,
    useDeleteSubjectMutation,
    useGetSubjectsQuery,
    useUpdateSubjectMutation,
} from 'src/api/endpoints/subjects/api';
import { ResponseError } from 'src/api';
import {
    getFormErrorMessage,
    isFormFieldValid,
} from 'src/pages/Materials/components/MaterialEditingPopup/Content/MaterialEditingPopupContent';
import { AuthorizedUserProps } from 'src/routes/components/PrivateRoute';
import { UserRole } from 'src/api/endpoints/users/users.types.new';

export const SubjectsPage: FC<AuthorizedUserProps> = (
    props: AuthorizedUserProps
): ReactElement => {
    const canModifyMaterials: boolean =
        props.permissions.canBeEditMaterials ||
        props.role === UserRole.OrganizationDirector;

    const [modal, setModal] = useState(false);
    const toast = useRef<Toast>(null);

    const { data: subjectsResponse, isLoading: isSubjectsLoading } =
        useGetSubjectsQuery();

    const [createSubject] = useCreateSubjectMutation();
    const [updateSubject] = useUpdateSubjectMutation();
    const [deleteSubject] = useDeleteSubjectMutation();

    const onSubjectCreate = async (values: SubjectCreate): Promise<void> => {
        return createSubject({ body: values })
            .unwrap()
            .then((): void => {
                notifyAboutSuccess({
                    toast,
                    content: 'Предмет создан',
                    sound: false,
                });
            })
            .catch((error: ResponseError): void => {
                notifyAboutException({ toast, content: error.message });
            });
    };

    const onSubjectDelete = async (subjectId: number): Promise<void> => {
        return deleteSubject({ params: { subjectId } })
            .unwrap()
            .then((): void => {
                notifyAboutSuccess({
                    toast,
                    content: 'Предмет удален',
                    sound: false,
                });
            })
            .catch((error: ResponseError): void => {
                notifyAboutException({ toast, content: error.message });
            });
    };

    const onSubjectUpdate = async (
        subjectId: number,
        values: SubjectUpdateForm
    ): Promise<void> => {
        return updateSubject({ params: { subjectId }, body: values })
            .unwrap()
            .then((): void => {
                notifyAboutSuccess({
                    toast,
                    content: 'Информация о предмете обновлена',
                    sound: false,
                });
            })
            .catch((error: ResponseError): void => {
                notifyAboutException({ toast, content: error.message });
            });
    };

    return (
        <StudentLayout auth={props}>
            <Helmet title={'Предметы'} />
            <Toast ref={toast} />

            {canModifyMaterials && (
                <LegacyModal
                    header={'Создание предмета'}
                    visible={modal}
                    onHide={() => setModal(false)}
                >
                    <SubjectCreateForm onSubmit={onSubjectCreate} />
                </LegacyModal>
            )}

            <Card headerClassName={'mt-24'} header={'Предметы'}>
                {isSubjectsLoading ? (
                    <Loader />
                ) : (
                    <DataTable
                        header={
                            canModifyMaterials ? (
                                <Toolbar
                                    add={{ onClick: () => setModal(true) }}
                                />
                            ) : null
                        }
                        value={subjectsResponse ?? []}
                    >
                        <Column
                            filter
                            sortable
                            field={'name'}
                            header="Название"
                        />
                        <Column
                            body={(column: { id: number; name: string }) => {
                                if (canModifyMaterials)
                                    return (
                                        <SubjectUpdateButton
                                            id={column.id}
                                            onSubmit={onSubjectUpdate}
                                        />
                                    );
                            }}
                        />
                        <Column
                            body={(column: { id: number }) => {
                                if (canModifyMaterials)
                                    return (
                                        <DeleteButton
                                            onClick={() =>
                                                onSubjectDelete(column.id)
                                            }
                                        />
                                    );
                            }}
                        />
                    </DataTable>
                )}
            </Card>
        </StudentLayout>
    );
};

export type SubjectCreateFormProps = {
    onSubmit: (values: SubjectCreate) => Promise<void>;
};

const SubjectCreateForm: FC<SubjectCreateFormProps> = (
    props: SubjectCreateFormProps
) => {
    const { onSubmit } = props;

    const [isLoading, setLoading] = useState(false);

    const formik: Formik<Partial<SubjectCreate>> = useFormik({
        initialValues: {},
        validate: ({ name }: Partial<SubjectCreate>): FormikErrors<any> => {
            const errors: FormikErrors<Partial<SubjectCreate>> = {};

            if (!name || !name.trim()) errors.name = requireField;

            return errors;
        },
        onSubmit: async (values: Partial<SubjectCreate>): Promise<void> => {
            setLoading(true);

            if (onSubmit) {
                await onSubmit(values as SubjectCreate);
            }

            setLoading(false);
        },
    });

    return (
        <form onSubmit={formik.handleSubmit} className="p-fluid">
            <div className="field">
                <InputText
                    id="name"
                    name="name"
                    className={classNames({
                        'p-invalid': isFormFieldValid(formik, 'name'),
                    })}
                    onChange={formik.handleChange}
                    value={formik.values.name}
                    placeholder={'Наименование'}
                />
                {getFormErrorMessage(formik, 'name')}
            </div>
            <Button
                loading={isLoading}
                type="submit"
                label="Сохранить"
                className={'w-auto'}
            />
        </form>
    );
};
